import { spanText } from '../../shared/lib/format';
import type { TAgentRecord, TAgentSession } from './api';

export type TRecordFilter = 'all' | 'tools' | 'thinking';

// `get_order_status` reads "Get order status": the name the model sees, said as words.
export const toolWords = (name: string): string => {
  const words = name.replace(/__/g, ' · ').replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const LIMIT_TITLE: Record<string, string> = {
  limit_steps: 'Stopped by the step limit',
  limit_cost: 'Stopped by the cost limit',
  limit_time: 'Stopped by the time limit',
  limit_budget: 'Stopped: the AI budget ran out',
  limit_queue: 'Stopped: waited too long for its turn',
};

const answered = (records: readonly TAgentRecord[], record: TAgentRecord): boolean =>
  records.some((each) => each.kind === 'answer' && each.turn === record.turn);

export const recordTitle = (
  record: TAgentRecord,
  records: readonly TAgentRecord[],
  session?: TAgentSession,
): string => {
  switch (record.kind) {
    case 'model_call':
      return answered(records, record) ? 'Writing the answer' : 'Thinking';
    case 'tool_call':
      return record.tool ? toolWords(record.tool.name) : 'A tool';
    case 'answer':
      return 'Answer';
    case 'limit':
      return LIMIT_TITLE[session?.endReason ?? ''] ?? 'Stopped by a limit';
    case 'wait':
      return record.tool ? `${toolWords(record.tool.name)} · asks first` : 'Waited';
    default:
      return record.note?.kind === 'held' ? 'Held by the AI budget' : 'The run went on';
  }
};

export const isError = (record: TAgentRecord): boolean =>
  record.status === 'failed' || record.status === 'outcome_unknown';

export const isWait = (record: TAgentRecord): boolean =>
  record.kind === 'wait' ||
  record.status === 'waiting' ||
  record.status === 'still_running' ||
  record.links.childRunId !== undefined ||
  record.links.jobId !== undefined;

export const matchesFilter = (record: TAgentRecord, filter: TRecordFilter): boolean =>
  filter === 'all' ||
  (filter === 'tools' && (record.kind === 'tool_call' || record.kind === 'wait')) ||
  (filter === 'thinking' && record.kind === 'model_call');

/** What the chips count: model and tool calls and their waits; the answer and limit in All. */
export const filterCounts = (records: readonly TAgentRecord[]): Record<TRecordFilter, number> => ({
  all: records.filter((record) => record.kind !== 'note').length,
  tools: records.filter((record) => matchesFilter(record, 'tools')).length,
  thinking: records.filter((record) => matchesFilter(record, 'thinking')).length,
});

export interface IJumps {
  errors: TAgentRecord[];
  waits: TAgentRecord[];
  answer: TAgentRecord | null;
}

export const jumpsOf = (records: readonly TAgentRecord[]): IJumps => ({
  errors: records.filter(isError),
  waits: records.filter(isWait),
  answer: records.find((record) => record.kind === 'answer' || record.kind === 'limit') ?? null,
});

/** "What the agent made of it": the model call that read this turn's results. */
export const madeOf = (
  records: readonly TAgentRecord[],
  record: TAgentRecord,
): TAgentRecord | null =>
  record.kind === 'tool_call'
    ? (records.find((each) => each.kind === 'model_call' && each.turn === record.turn + 1) ?? null)
    : null;

export const durationOf = (record: TAgentRecord): number | null => {
  const waited = record.links.childRunId !== undefined || record.links.jobId !== undefined;
  if (record.durationMs !== null && !(waited && record.durationMs === 0)) return record.durationMs;
  // Cut off in flight: from its start to the next worker's verdict is a crash, not a call.
  if (record.status === 'outcome_unknown') return null;
  if (!record.startedAt || !record.finishedAt) return null;
  return new Date(record.finishedAt).getTime() - new Date(record.startedAt).getTime();
};

export const tookText = (record: TAgentRecord): string => {
  const ms = durationOf(record);
  return ms === null ? '—' : spanText(ms);
};

// Cents hide an agent's cost ($0.004): three decimals under a dollar, cents above.
export const costText = (usd: number): string =>
  usd >= 1
    ? `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;

export type TTraceRow =
  | { kind: 'record'; record: TAgentRecord }
  | { kind: 'group'; key: string; from: number; to: number; records: TAgentRecord[] };

const COLLAPSE_OVER = 12;
const MIN_GROUP = 3;

// Board 32: a long run folds its ordinary middle; errors, waits, links, notes, the answer and
// their neighbours stay open, and so do the first four and the chosen one.
export const traceRows = (
  records: readonly TAgentRecord[],
  keep: ReadonlySet<number>,
): TTraceRow[] => {
  if (records.length <= COLLAPSE_OVER) return records.map((record) => ({ kind: 'record', record }));
  const marked = records.map(
    (record, at) =>
      at < 4 ||
      at === records.length - 1 ||
      keep.has(record.seq) ||
      (record.kind !== 'model_call' && record.kind !== 'tool_call') ||
      isError(record) ||
      isWait(record) ||
      record.links.retryOf !== undefined ||
      record.resumed === true,
  );
  const open = marked.map((on, at) => on || marked[at - 1] === true || marked[at + 1] === true);
  const rows: TTraceRow[] = [];
  let run: TAgentRecord[] = [];
  const flush = () => {
    if (run.length >= MIN_GROUP) {
      rows.push({
        kind: 'group',
        key: `g${run[0].seq}`,
        from: run[0].seq,
        to: run.at(-1)!.seq,
        records: run,
      });
    } else rows.push(...run.map((record) => ({ kind: 'record' as const, record })));
    run = [];
  };
  records.forEach((record, at) => {
    if (open[at]) {
      flush();
      rows.push({ kind: 'record', record });
    } else run.push(record);
  });
  flush();
  return rows;
};

/** "FilterLogEvents ×5 · Search events ×3 · thinking ×4". */
export const groupWords = (records: readonly TAgentRecord[]): string => {
  const counts = new Map<string, number>();
  for (const record of records) {
    const name =
      record.kind === 'tool_call' && record.tool ? toolWords(record.tool.name) : 'thinking';
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts]
    .sort((a, b) => (a[0] === 'thinking' ? 1 : b[0] === 'thinking' ? -1 : b[1] - a[1]))
    .map(([name, count]) => `${name} ×${count}`)
    .join(' · ');
};

export const groupCost = (records: readonly TAgentRecord[]): number =>
  records.reduce((sum, record) => sum + (record.costUsd ?? 0), 0);

export const groupTook = (records: readonly TAgentRecord[]): number =>
  records.reduce((sum, record) => sum + (durationOf(record) ?? 0), 0);

const STATUS_WORDS: Record<string, string> = {
  pending: 'waiting its turn',
  running: 'running',
  in_flight: 'sent, waiting for the answer',
  retrying: 'failed, trying again',
  waiting: 'waiting',
  succeeded: 'ran',
  failed: 'failed',
  outcome_unknown: 'Outcome unknown',
  still_running: 'still running',
  described: 'not run in a test',
  skipped: 'not run',
};

export const statusWords = (status: string): string => STATUS_WORDS[status] ?? status;

const SESSION_WORDS: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  waiting: 'Waiting',
  held: 'Paused · AI budget',
  done: 'Finished · done',
  limit: 'Finished · hit a limit',
  failed: 'Failed',
  stopped: 'Stopped',
};

export const sessionWords = (session: TAgentSession): string =>
  SESSION_WORDS[session.state] ?? session.state;

export const isOpen = (session: TAgentSession): boolean =>
  ['queued', 'running', 'waiting', 'held'].includes(session.state);

export const jsonText = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2) ?? '';
};

/** One line of JSON for a record's row: `{ "order_id": "LG-10482" }`. */
export const inlineJson = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  try {
    return (JSON.stringify(value) ?? '').replace(/":/g, '": ').replace(/,"/g, ', "');
  } catch {
    return String(value);
  }
};
